import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, LogIn } from 'lucide-react';
import Toast from '../components/Toast';

const API_BASE_URL = '/api';

interface InviteInfo {
    groupId: string;
    name: string;
    description?: string;
    groupIcon?: string;
    memberCount: number;
    maxMembers: number;
}

const InvitePage: React.FC = () => {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    useEffect(() => {
        const fetchInvite = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/groups/invite/${code}`);
                if (res.ok) {
                    setInviteInfo(await res.json());
                } else {
                    const err = await res.json();
                    setError(err.message || 'Convite inválido');
                }
            } catch {
                setError('Erro ao carregar convite');
            } finally {
                setLoading(false);
            }
        };
        if (code) fetchInvite();
    }, [code]);

    const handleJoin = async () => {
        if (!user) {
            // Guarda o código e manda para login
            navigate(`/login?invite=${code}`);
            return;
        }

        setJoining(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/groups/join/${code}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
                const group = await res.json();
                setToast({ message: 'Você entrou no grupo!', type: 'success' });
                setTimeout(() => navigate(`/groups/${group._id || inviteInfo?.groupId}`), 1200);
            } else {
                const err = await res.json();
                setToast({ message: err.message || 'Erro ao entrar no grupo', type: 'error' });
            }
        } catch {
            setToast({ message: 'Erro ao entrar no grupo', type: 'error' });
        } finally {
            setJoining(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.spinner} />
                    <p style={styles.hint}>Carregando convite...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <p style={styles.errorText}>{error}</p>
                    <button style={styles.btnSecondary} onClick={() => navigate('/')}>
                        Ir para o início
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                {inviteInfo?.groupIcon ? (
                    <img src={inviteInfo.groupIcon} alt={inviteInfo.name} style={styles.icon} />
                ) : (
                    <div style={styles.iconPlaceholder}>
                        <Users size={32} color="#2F7130" />
                    </div>
                )}

                <p style={styles.label}>Você foi convidado para entrar no grupo</p>
                <h1 style={styles.groupName}>{inviteInfo?.name}</h1>

                {inviteInfo?.description && (
                    <p style={styles.description}>{inviteInfo.description}</p>
                )}

                <div style={styles.memberPill}>
                    <Users size={14} />
                    <span>{inviteInfo?.memberCount} / {inviteInfo?.maxMembers} membros</span>
                </div>

                <div style={styles.actions}>
                    <button style={styles.btnSecondary} onClick={() => navigate('/')}>
                        Recusar
                    </button>
                    <button
                        style={styles.btnPrimary}
                        onClick={handleJoin}
                        disabled={joining}
                    >
                        {!user && <LogIn size={16} />}
                        {joining ? 'Entrando...' : user ? 'Entrar no grupo' : 'Fazer login e entrar'}
                    </button>
                </div>
            </div>

            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F5F7F5',
        padding: '24px',
    },
    card: {
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        padding: '40px 32px',
        maxWidth: '420px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 4px 24px rgba(5,29,4,0.08)',
    },
    icon: {
        width: '72px',
        height: '72px',
        borderRadius: '14px',
        objectFit: 'cover',
        border: '1px solid #E5E7EB',
        marginBottom: '4px',
    },
    iconPlaceholder: {
        width: '72px',
        height: '72px',
        borderRadius: '14px',
        background: 'rgba(47,113,48,0.08)',
        border: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4px',
    },
    label: {
        fontSize: '13px',
        color: '#6B7280',
        margin: 0,
        textAlign: 'center',
    },
    groupName: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#051D04',
        margin: '4px 0',
        textAlign: 'center',
        letterSpacing: '-0.4px',
    },
    description: {
        fontSize: '14px',
        color: '#6B7280',
        margin: 0,
        textAlign: 'center',
        lineHeight: 1.5,
    },
    memberPill: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        background: '#F5F7F5',
        border: '1px solid #E5E7EB',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#6B7280',
        marginTop: '4px',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginTop: '12px',
        width: '100%',
    },
    btnPrimary: {
        flex: 1,
        padding: '12px 20px',
        background: '#2F7130',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    },
    btnSecondary: {
        flex: 1,
        padding: '12px 20px',
        background: '#fff',
        color: '#6B7280',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
    },
    hint: {
        fontSize: '14px',
        color: '#6B7280',
    },
    errorText: {
        fontSize: '15px',
        color: '#DC2626',
        textAlign: 'center',
        fontWeight: 500,
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid rgba(47,113,48,0.2)',
        borderTopColor: '#2F7130',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
};

export default InvitePage;